import { jsxs, jsx } from 'react/jsx-runtime';
import { IconCheck } from '@wds/icons';
import { Typography } from '@wds/typography';
import { defaultBaseline, defaultFonts, visuallyHidden, uniqueId, clsx, safeRest } from '@wds/utils';
import { useMemo } from 'react';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    type = theme.type,
    spacing = theme.spacing,
    motion = theme.motion,
    radius = theme.radius,
    shadow = theme.shadow;
  var checkbox = {
    bgColorOn: palette.primary.main,
    iconColorOn: palette.primary.contrast
  };
  var bounceFrames = {
    '@keyframes bounce': {
      '0%': {
        opacity: 0,
        transform: 'scale(0.6)',
        animationTimingFunction: 'cubic-bezier(0, 0, .1, 1)'
      },
      '10%': {
        opacity: 1
      },
      '33%': {
        opacity: 1,
        transform: 'scale(1.4)',
        animationTimingFunction: 'cubic-bezier(.2, 0, 0, 1)'
      },
      '100%': {
        opacity: 1,
        transform: 'scale(1)'
      }
    }
  };
  var scaleDownFrames = {
    '@keyframes scaleDown': {
      from: {
        opacity: 1,
        transform: 'scale(1)',
        animationTimingFunction: 'cubic-bezier(1, 0, 1, 1)'
      },
      '75%': {
        opacity: 1
      },
      '100%': {
        opacity: 0,
        transform: 'scale(0.4)'
      }
    }
  };
  return __assign(__assign(__assign({}, bounceFrames), scaleDownFrames), {
    wrenchCheckbox: function () {
      return __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
        position: 'relative',
        display: 'inline-flex',
        minHeight: spacing[5],
        minWidth: spacing[5],
        lineHeight: "".concat(spacing[5], "px")
      });
    },
    wrenchCheckboxFaux: function () {
      return __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
        content: '""',
        display: 'inline-block',
        height: spacing[5],
        width: spacing[5],
        flexGrow: 0,
        flexShrink: 0,
        fontSize: type.fontSize.body,
        border: "1px solid ".concat(palette.border.main),
        background: palette.background.light,
        borderRadius: radius.small,
        transition: "all 50ms ".concat(motion.curve.exit, " 100ms"),
        verticalAlign: 'top',
        textAlign: 'left',
        '&:empty::after': {
          content: '3',
          fontFamily: type.fontFamily.icon,
          color: checkbox.iconColorOn,
          lineHeight: 1
        },
        '> svg[class^="wrenchIcons"], &:empty::after': {
          position: 'absolute',
          top: spacing[1],
          left: spacing[1],
          opacity: 0,
          textAlign: 'center',
          fill: checkbox.iconColorOn,
          animation: '$scaleDown 150ms forwards'
        }
      });
    },
    wrenchCheckboxLabel: function () {
      return {
        padding: [0, 0, 0, spacing[4]],
        whiteSpace: 'normal'
      };
    },
    wrenchCheckboxIndeterminate: function () {
      return {
        '& + $wrenchCheckboxFaux': {
          borderColor: checkbox.bgColorOn,
          background: checkbox.bgColorOn,
          transform: 'scale(1)',
          transition: "transform 200ms ".concat(motion.curve.bounceEnd)
        },
        '& + $wrenchCheckboxFaux > svg[class^="wrenchIcons"]': visuallyHidden,
        '& + $wrenchCheckboxFaux:after': {
          position: 'absolute',
          top: '10px',
          left: '5px',
          display: 'block',
          content: '""',
          background: palette.background.light,
          width: spacing[3],
          height: '2px'
        }
      };
    },
    wrenchCheckboxNative: function () {
      return __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
        position: 'absolute',
        cursor: 'inherit',
        opacity: 0,
        width: spacing[5],
        height: spacing[5],
        zIndex: 1,
        left: 0,
        '&:focus:checked + $wrenchCheckboxFaux': {
          borderColor: '#fff !important',
          boxShadow: "0 0 0 1px ".concat(palette.primary.main, " !important")
        },
        '&:focus + $wrenchCheckboxFaux, &:hover + $wrenchCheckboxFaux': {
          borderColor: palette.border.hover,
          boxShadow: shadow.sky,
          transform: 'scale(1.1)',
          transition: ["transform 200ms ".concat(motion.curve.ease), 'border-color 75ms linear 50ms', 'box-shadow 75ms linear 50ms']
        },
        '&:active + $wrenchCheckboxFaux': {
          transform: 'scale(0.7)',
          transition: "transform 100ms ".concat(motion.curve.bounceStart)
        },
        '&:checked + $wrenchCheckboxFaux': {
          borderColor: checkbox.bgColorOn,
          background: checkbox.bgColorOn,
          transform: 'scale(1)',
          transition: "transform 200ms ".concat(motion.curve.bounceEnd)
        },
        '&:checked + $wrenchCheckboxFaux > svg[class^="wrenchIcons"]': {
          animation: "$bounce 200ms forwards"
        },
        '&:checked + $wrenchCheckboxFaux &:empty::after': {
          animation: "$bounce 200ms forwards"
        },
        '&:disabled + $wrenchCheckboxFaux, &:disabled ~ $wrenchCheckboxLabel': {
          opacity: '0.5',
          transform: 'scale(1)'
        }
      });
    }
  });
});

var Checkbox = function Checkbox(_a) {
  var ariaLabel = _a.ariaLabel,
    checked = _a.checked,
    defaultChecked = _a.defaultChecked,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    id = _a.id,
    _c = _a.indeterminate,
    indeterminate = _c === void 0 ? false : _c,
    innerRef = _a.innerRef,
    label = _a.label,
    name = _a.name,
    onBlur = _a.onBlur,
    onChange = _a.onChange,
    rest = __rest(_a, ["ariaLabel", "checked", "defaultChecked", "disabled", "id", "indeterminate", "innerRef", "label", "name", "onBlur", "onChange"]);
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('checkbox');
  }, [id]);
  var _d = useStyles(),
    wrenchCheckboxNative = _d.wrenchCheckboxNative,
    wrenchCheckboxIndeterminate = _d.wrenchCheckboxIndeterminate,
    wrenchCheckbox = _d.wrenchCheckbox,
    wrenchCheckboxLabel = _d.wrenchCheckboxLabel,
    wrenchCheckboxFaux = _d.wrenchCheckboxFaux;
  var nativeClasses = clsx(wrenchCheckboxNative, indeterminate && wrenchCheckboxIndeterminate || {});
  var handleBlur = function handleBlur(e) {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  };
  var handleChange = function handleChange(e) {
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };
  return jsxs("span", {
    className: wrenchCheckbox,
    children: [jsx("input", __assign({
      "aria-label": ariaLabel,
      className: nativeClasses,
      checked: checked,
      defaultChecked: defaultChecked,
      disabled: disabled,
      id: inputID,
      name: name,
      onBlur: handleBlur,
      onChange: handleChange,
      tabIndex: disabled ? -1 : 0,
      type: "checkbox",
      ref: innerRef
    }, safeRest(rest))), jsx("span", {
      className: wrenchCheckboxFaux,
      "aria-hidden": "true",
      children: jsx(IconCheck, {
        color: "light",
        "aria-hidden": "true",
        title: "Check"
      })
    }), label && jsx("span", {
      className: wrenchCheckboxLabel,
      children: jsx("label", {
        htmlFor: inputID,
        children: jsx(Typography, {
          variant: "body",
          component: "span",
          children: label
        })
      })
    })]
  });
};

export { Checkbox };
